import React, { useEffect, useState } from 'react';
import {
  Edit3, Trash2, RefreshCw, Clock, Copy,
  ChevronRight, ChevronDown, ChevronUp, GripVertical,
} from 'lucide-react';
import EnableToggle from '../ui/EnableToggle';
import { isWhiteLabel } from '../../config/tenantConfig';
import { StrategyMetadata, StrategyWithLegs } from '../../types';
import { getDerivedTradingType, getDerivedTradingTypeShort } from '../../utils/strategy/strategyDisplayHelpers';
import { expiryTypeLabel } from '../../utils/strategy/expiryDisplay';
import {
  DAY,
  DAY_SHORT_LABELS,
  WEEKDAYS,
  ENTRY_SELECTION_MODE,
  INTRADAY_DTE_OPTIONS,
  INTRADAY_DTE_EDIT_LABELS,
  INTRADAY_DTE_COMPACT_LABELS,
  MONTHLY_ONLY_UNDERLYINGS,
  EXPIRY_TYPE_CONST,
} from '../../constants/tradingConstants';
import strategyService from '../../services/strategyService';
import { cn } from '../../utils/cn';
import { CHIP_ACCENT } from '../../utils/chipColors';
import { FEATURE_TONES, type FeatureChipTone } from '../../utils/strategyFeatureChips';
import StrategyEditorPanel from './StrategyEditorPanel';
import ConfirmDialog from '../common/ConfirmDialog';

const wl = isWhiteLabel();
const BRAND_BG = wl ? 'bg-zebu-600' : 'bg-blue-600';
const BRAND_BORDER = wl ? 'border-zebu-600' : 'border-blue-600';
const BRAND_TEXT_HOVER = wl ? 'hover:text-zebu-600 dark:hover:text-zebu-400' : 'hover:text-blue-600 dark:hover:text-blue-400';
const BRAND_TEXT = wl ? 'text-zebu-500' : 'text-blue-500';
const BRAND_PILL = wl
  ? 'bg-zebu-100 dark:bg-zebu-900/40 text-zebu-700 dark:text-zebu-300'
  : 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300';
const BRAND_BTN_HOVER_TONE = wl
  ? 'hover:text-zebu-600 hover:bg-zebu-100 dark:hover:bg-zebu-900/30'
  : 'hover:text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/30';
const BRAND_DIVIDER = wl ? 'border-zebu-100 dark:border-zebu-900/40' : 'border-blue-100 dark:border-blue-900/40';
const BRAND_ACTIVE_PILL = wl
  ? 'bg-zebu-600 text-white border-zebu-600 hover:bg-zebu-700 shadow-sm'
  : 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700 shadow-sm';

// ─── constants ───────────────────────────────────────────────────────────────

const DAYS = Object.values(DAY) as (keyof typeof DAY)[];

// ─── helpers ─────────────────────────────────────────────────────────────────

const toHHMM = (t?: string) => (t ? t.slice(0, 5) : '');

// Strategy-level feature chips — FEATURE_TONES + type extracted to
// `utils/strategyFeatureChips.ts` so the invariant test can import without
// dragging in this component's transitive service dependencies (axios ESM
// breaks under Jest). See that file for cross-app consistency rationale.
const FeatureChip: React.FC<{ tone: FeatureChipTone; title?: string; children: React.ReactNode }> = ({ tone, title, children }) => (
  <span title={title} className={cn('inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-extrabold tracking-wide border', FEATURE_TONES[tone])}>
    {children}
  </span>
);

// Strategy-level MTM target/stop → compact chip text (₹ for TOTAL_MTM, % for
// premium-percent). Returns null when disabled / unset (value ≤ 0).
const fmtStrategyMtm = (cfg?: { enabled?: boolean; type?: string; value?: number }): string | null => {
  if (!cfg || typeof cfg.value !== 'number' || cfg.value <= 0 || cfg.enabled === false) return null;
  return cfg.type === 'COMBINED_PREMIUM_PERCENT' ? `${cfg.value}%` : `₹${cfg.value}`;
};

// ─── main component ───────────────────────────────────────────────────────────

interface StrategyRowViewProps {
  strategies: StrategyMetadata[];
  /** Basket the strategies belong to — needed by the inline editor panel. */
  basketId: string;
  /** Called after an inline edit saves, so the parent can refetch strategies. */
  onAfterSave?: () => void | Promise<void>;
  onEdit: (strategy: StrategyMetadata) => void;
  onDelete: (strategy: StrategyMetadata) => void;
  onCopy?: (strategy: StrategyMetadata) => void;
  onStatusToggle?: (strategy: StrategyMetadata) => void;
  /**
   * Called when the user toggles a weekday chip, a DTE chip, or the Weekday/DTE
   * segmented control. Carries the full entry-selection payload so the backend
   * can persist the active mode + both selections (the inactive mode's chips
   * are preserved so users can flip modes without losing their picks).
   */
  onEntrySelectionUpdate: (
    strategyId: string,
    payload: {
      entrySelectionMode: 'WEEKDAY' | 'DTE';
      entryDays: string[];
      entryDtes: number[];
    },
  ) => Promise<void>;
  updatingStrategy: string | null;
  /** Called with the new full ordered list of strategyIds on drop or arrow-tap.
   *  When undefined, reorder controls are not rendered. */
  onReorder?: (strategyIds: string[]) => Promise<void> | void;
  /** True when the parent is showing strategies in custom (user-defined) order.
   *  Controls visibility of drag handles and mobile arrows. */
  reorderEnabled?: boolean;
  /** True on desktop viewports; controls whether to render drag handles vs
   *  mobile up/down arrow buttons. */
  isDesktop?: boolean;
}

const StrategyRowView: React.FC<StrategyRowViewProps> = ({
  strategies, basketId, onAfterSave, onEdit, onDelete, onCopy, onStatusToggle, onEntrySelectionUpdate, updatingStrategy,
  onReorder, reorderEnabled = false, isDesktop = true,
}) => {
  const showReorder = reorderEnabled && !!onReorder;
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const fireReorder = (ordered: StrategyMetadata[]) => {
    if (!onReorder) return;
    void onReorder(ordered.map(s => s.strategyId));
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', String(index)); } catch {}
  };
  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };
  const handleDragLeave = (index: number) => {
    setDragOverIndex(prev => (prev === index ? null : prev));
  };
  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    const from = draggedIndex;
    setDraggedIndex(null);
    setDragOverIndex(null);
    if (from === null || from === dropIndex) return;
    const reordered = [...strategies];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(dropIndex, 0, moved);
    fireReorder(reordered);
  };
  const handleMove = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= strategies.length) return;
    const reordered = [...strategies];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    fireReorder(reordered);
  };

  // row-level
  const [localDays, setLocalDays] = useState<Record<string, string[]>>(() =>
    Object.fromEntries(strategies.map(s => [s.strategyId, s.entryDays ?? WEEKDAYS]))
  );
  // Both fields persist across mode toggles, so localMode + localDtes track the
  // user's DTE selections separately from the weekday selections. Defaults
  // preserve back-compat behaviour: undefined mode → "WEEKDAY".
  const [localMode, setLocalMode] = useState<Record<string, 'WEEKDAY' | 'DTE'>>(() =>
    Object.fromEntries(strategies.map(s => [s.strategyId, s.entrySelectionMode === 'DTE' ? 'DTE' : 'WEEKDAY']))
  );
  const [localDtes, setLocalDtes] = useState<Record<string, number[]>>(() =>
    Object.fromEntries(strategies.map(s => [s.strategyId, s.entryDtes ?? []]))
  );

  // Keep localDays/localMode/localDtes seeded for newly added strategies (e.g.
  // right after create) without overwriting user-toggled values for existing rows.
  useEffect(() => {
    setLocalDays(prev => {
      const next = { ...prev };
      for (const s of strategies) {
        if (!next[s.strategyId]) {
          next[s.strategyId] = s.entryDays ?? WEEKDAYS;
        }
      }
      return next;
    });
    setLocalMode(prev => {
      const next = { ...prev };
      for (const s of strategies) {
        if (!next[s.strategyId]) {
          next[s.strategyId] = s.entrySelectionMode === 'DTE' ? 'DTE' : 'WEEKDAY';
        }
      }
      return next;
    });
    setLocalDtes(prev => {
      const next = { ...prev };
      for (const s of strategies) {
        if (!next[s.strategyId]) {
          next[s.strategyId] = s.entryDtes ?? [];
        }
      }
      return next;
    });
  }, [strategies]);

  // expand panel — hosts the full StrategyEditorPanel inline
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [loadingExpand, setLoadingExpand] = useState<string | null>(null);
  const [fullData, setFullData] = useState<Record<string, StrategyWithLegs>>({});
  // Unsaved-edit guard: the inline editor reports its dirty state here; any path
  // that would discard those edits (collapse, switch row, Cancel) confirms first.
  const [editorDirty, setEditorDirty] = useState(false);
  const [pendingDiscard, setPendingDiscard] =
    useState<
      | { type: 'toggle'; strategy: StrategyMetadata }
      | { type: 'cancel' }
      | null
    >(null);


  // ── handlers ────────────────────────────────────────────────────────────

  // DTE-mode eligibility predicate. Mirrors the backend validator
  // (strategy_manager._validate_schedule_fields) and the in-render dteAllowed
  // check so the click handler can never silently ship a DTE+non-weekly combo.
  const isDteEligible = (strategy: StrategyMetadata) =>
    strategy.tradingType !== 'POSITIONAL' &&
    strategy.expiryType === EXPIRY_TYPE_CONST.WEEKLY &&
    !!strategy.underlying &&
    !MONTHLY_ONLY_UNDERLYINGS.includes(strategy.underlying);

  // Build the payload the backend expects — always carries both modes' chips
  // so toggling Weekday ↔ DTE doesn't drop the inactive selection. Coerces
  // mode to WEEKDAY whenever the strategy isn't DTE-eligible so a stale
  // localMode value (left over from before an expiry change) can't escape.
  const buildEntryPayload = (
    strategy: StrategyMetadata,
    overrides: Partial<{ mode: 'WEEKDAY' | 'DTE'; days: string[]; dtes: number[] }>,
  ) => {
    const id = strategy.strategyId;
    const rawMode = overrides.mode ?? (localMode[id] ?? 'WEEKDAY');
    const mode: 'WEEKDAY' | 'DTE' = isDteEligible(strategy) ? rawMode : 'WEEKDAY';
    return {
      entrySelectionMode: mode,
      entryDays: overrides.days ?? (localDays[id] ?? WEEKDAYS),
      entryDtes: overrides.dtes ?? (localDtes[id] ?? []),
    };
  };

  const handleDayToggle = async (strategy: StrategyMetadata, day: string) => {
    const current = localDays[strategy.strategyId] ?? [];
    const updated = current.includes(day) ? current.filter(d => d !== day) : [...current, day];
    setLocalDays(prev => ({ ...prev, [strategy.strategyId]: updated }));
    try { await onEntrySelectionUpdate(strategy.strategyId, buildEntryPayload(strategy, { days: updated })); }
    catch { setLocalDays(prev => ({ ...prev, [strategy.strategyId]: current })); }
  };

  const handleDteToggle = async (strategy: StrategyMetadata, dte: number) => {
    const current = localDtes[strategy.strategyId] ?? [];
    const updated = current.includes(dte)
      ? current.filter(d => d !== dte)
      : [...current, dte].sort((a, b) => a - b);
    setLocalDtes(prev => ({ ...prev, [strategy.strategyId]: updated }));
    try { await onEntrySelectionUpdate(strategy.strategyId, buildEntryPayload(strategy, { dtes: updated })); }
    catch { setLocalDtes(prev => ({ ...prev, [strategy.strategyId]: current })); }
  };

  const handleModeToggle = async (strategy: StrategyMetadata, nextMode: 'WEEKDAY' | 'DTE') => {
    const current = localMode[strategy.strategyId] ?? 'WEEKDAY';
    if (nextMode === current) return;
    // First-time switch to DTE with no prior chip selection: default to all 5
    // DTEs, mirroring the WEEKDAY default of all 5 weekdays. Functionally
    // equivalent to "fires every weekday" since today's DTE is always 0-4 in
    // the current-weekly chain. Subsequent toggles preserve user's picks.
    const currentDtes = localDtes[strategy.strategyId] ?? [];
    const dtesForPayload = nextMode === 'DTE' && currentDtes.length === 0
      ? [...INTRADAY_DTE_OPTIONS]
      : currentDtes;
    setLocalMode(prev => ({ ...prev, [strategy.strategyId]: nextMode }));
    if (dtesForPayload !== currentDtes) {
      setLocalDtes(prev => ({ ...prev, [strategy.strategyId]: dtesForPayload }));
    }
    try {
      await onEntrySelectionUpdate(strategy.strategyId, buildEntryPayload(strategy, {
        mode: nextMode,
        dtes: dtesForPayload,
      }));
    } catch {
      setLocalMode(prev => ({ ...prev, [strategy.strategyId]: current }));
      if (dtesForPayload !== currentDtes) {
        setLocalDtes(prev => ({ ...prev, [strategy.strategyId]: currentDtes }));
      }
    }
  };

  const doToggleExpand = async (strategy: StrategyMetadata) => {
    const id = strategy.strategyId;
    if (expandedId === id) { setExpandedId(null); setEditorDirty(false); return; }
    setExpandedId(id);
    setEditorDirty(false);
    if (fullData[id]) return;
    setLoadingExpand(id);
    try {
      const data = await strategyService.getStrategy(id);
      setFullData(prev => ({ ...prev, [id]: data }));
    } catch { setExpandedId(null); }
    finally { setLoadingExpand(null); }
  };

  // Collapsing or switching away from a row with unsaved edits → confirm first.
  const handleToggleExpand = (strategy: StrategyMetadata) => {
    if (editorDirty && expandedId) { setPendingDiscard({ type: 'toggle', strategy }); return; }
    void doToggleExpand(strategy);
  };

  // The panel's Cancel button collapses the row — confirm if edits are pending.
  const handleEditorCancel = () => {
    if (editorDirty) { setPendingDiscard({ type: 'cancel' }); return; }
    setExpandedId(null);
    setEditorDirty(false);
  };

  // Edit / Copy / Delete are flat-out disabled when the row is expanded (see
  // actionBtns below), so the only dirty-guard paths left are: collapse-via-
  // chevron ('toggle') and Cancel-from-editor ('cancel').

  const confirmDiscard = () => {
    const p = pendingDiscard;
    setPendingDiscard(null);
    setEditorDirty(false);
    if (!p) return;
    if (p.type === 'cancel') { setExpandedId(null); return; }
    void doToggleExpand(p.strategy);
  };

  // After an inline save: keep the row expanded and refresh both the panel's
  // data and the parent list, so the user sees the saved values without having
  // to re-open the row.
  const handleInlineSaved = async (id: string) => {
    try {
      const data = await strategyService.getStrategy(id);
      setFullData(prev => ({ ...prev, [id]: data }));
    } catch { /* keep the in-memory copy if the refetch fails */ }
    if (onAfterSave) await onAfterSave();
  };

  // ── render ────────────────────────────────────────────────────────────────

  if (strategies.length === 0)
    return <div className="text-center py-12"><p className="text-sm text-gray-500 dark:text-gray-400">No strategies yet</p></div>;

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700">
      {/* Desktop header */}
      <div className="hidden xl:flex items-center gap-2 px-4 py-3 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 rounded-t-lg">
        <div className="w-20 shrink-0" />
        <div className="flex-1 min-w-[120px] text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Strategy</div>
        <div className="w-28 shrink-0 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Time</div>
        <div className="w-24 shrink-0 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Type</div>
        <div className="w-20 shrink-0 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Index</div>
        <div className="w-28 shrink-0 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Expiry</div>
        <div className="w-72 shrink-0 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Days</div>
        <div className="w-28 shrink-0" />
      </div>
      <div className="divide-y divide-gray-200 dark:divide-gray-700">
        {strategies.map((strategy, index) => {
          const id = strategy.strategyId;
          const isUpdating = updatingStrategy === id;
          const isExpanded = expandedId === id;
          const isLoadingExpand = loadingExpand === id;
          const days = localDays[id] ?? [];
          const mode = localMode[id] ?? 'WEEKDAY';
          const dtes = localDtes[id] ?? [];
          const typeFull = getDerivedTradingType(strategy);
          const typeShort = getDerivedTradingTypeShort(strategy);

          const expandChevron = (
            <button
              onClick={() => handleToggleExpand(strategy)}
              disabled={isLoadingExpand}
              className={`flex items-center justify-center text-gray-400 ${BRAND_TEXT_HOVER} transition-colors`}
              title={isExpanded ? 'Collapse' : 'Expand to edit'}
            >
              {isLoadingExpand
                ? <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                : isExpanded
                ? <ChevronDown className={`h-4 w-4 ${BRAND_TEXT}`} />
                : <ChevronRight className="h-4 w-4" />}
            </button>
          );

          const numberBadge = (
            <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${BRAND_PILL} text-xs font-bold shrink-0`}>
              {index + 1}
            </span>
          );

          const typeBadge = (
            <span title={typeFull} className={cn(
              'inline-flex items-center justify-center w-full h-8 px-2 rounded-md text-xs font-bold uppercase border border-cyan-200 dark:border-cyan-800/50',
              CHIP_ACCENT.TRADING_TYPE,
            )}>
              {typeShort}
            </span>
          );

          const indexBadge = strategy.underlying
            ? <span className={cn(
                'inline-flex items-center justify-center min-w-[76px] h-8 px-2 rounded-md text-xs font-semibold uppercase border border-violet-200 dark:border-violet-800/50',
                CHIP_ACCENT.UNDERLYING,
              )}>{strategy.underlying}</span>
            : <span className="text-xs text-gray-400">—</span>;

          const expiryBadge = strategy.expiryType
            ? <span className={cn(
                'flex items-center justify-center w-full h-8 px-2 rounded-md text-xs font-semibold uppercase border border-slate-200 dark:border-slate-700 whitespace-nowrap',
                CHIP_ACCENT.EXPIRY,
              )}>{expiryTypeLabel(strategy.expiryType)}</span>
            : <span className="text-xs text-gray-400">—</span>;

          // Strategy-level feature flags surfaced under the name (leg-level
          // features like WAT/TSL/Re-Entry aren't in the list payload).
          const tpText = fmtStrategyMtm(strategy.targetProfit);
          const slText = fmtStrategyMtm(strategy.stopLoss);
          const featureChips = (
            <>
              {strategy.moveSlToCost && <FeatureChip tone="mslc" title="Move SL to Cost">MSLC</FeatureChip>}
              {strategy.rangeBreakout && <FeatureChip tone="orb" title="Range Breakout (ORB)">ORB</FeatureChip>}
              {tpText && <FeatureChip tone="tp" title="Strategy target profit">TP {tpText}</FeatureChip>}
              {slText && <FeatureChip tone="sl" title="Strategy stop loss">SL {slText}</FeatureChip>}
              {strategy.hasUl && <FeatureChip tone="ul" title="Underlying-based risk features (spot-driven SL/TP/TSL/W&T)">UL</FeatureChip>}
            </>
          );

          // ORB range-end time — shown under the entry time only when Range Breakout is on.
          const rangeEnd = strategy.rangeBreakout && strategy.rangeBreakoutTime
            ? toHHMM(strategy.rangeBreakoutTime)
            : null;

          const isPositional = strategy.tradingType === 'POSITIONAL';
          const entryDTE = strategy.entryTradingDaysBeforeExpiry ?? 4;
          const exitDTE = strategy.exitTradingDaysBeforeExpiry ?? 0;
          // DTE mode is allowed only for current-weekly expiries on
          // weekly-capable underlyings (v1 scope). When gated out, the toggle
          // is hidden — the existing weekday row renders unchanged.
          const dteAllowed =
            !isPositional &&
            strategy.expiryType === EXPIRY_TYPE_CONST.WEEKLY &&
            !!strategy.underlying &&
            !MONTHLY_ONLY_UNDERLYINGS.includes(strategy.underlying);
          // When the active mode is DTE but the expiry/underlying becomes
          // ineligible (e.g. user flips to monthly), fall back to WEEKDAY view —
          // local state still preserves the DTE chips so flipping expiry back
          // to weekly restores them.
          const effectiveMode: 'WEEKDAY' | 'DTE' = dteAllowed ? mode : 'WEEKDAY';
          // Row-level quick-actions (chips, W/D pill, status toggle) are
          // disabled when the inline editor is open on this row. The form is
          // the source of truth in that mode — letting the chips stay live
          // creates two parallel editing surfaces that race on the same row.
          // `editLocked` is the OR of "in-flight save" and "editor expanded".
          const editLocked = isUpdating || isExpanded;
          // Compact inline W/D toggle — placed to the left of the chips, on
          // the same row, so the picker is one horizontal strip instead of
          // two stacked controls.
          const compactModeToggle = dteAllowed ? (
            <div
              role="tablist"
              aria-label="Entry selection mode"
              className={cn(
                "inline-flex rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden text-[10px] font-bold uppercase mr-1",
                isExpanded && "opacity-50",
              )}
            >
              {([ENTRY_SELECTION_MODE.WEEKDAY, ENTRY_SELECTION_MODE.DTE] as const).map((m) => {
                const active = effectiveMode === m;
                return (
                  <button
                    key={m}
                    role="tab"
                    aria-selected={active}
                    onClick={() => handleModeToggle(strategy, m)}
                    disabled={editLocked}
                    title={isExpanded
                      ? 'Locked — close the editor to change mode here'
                      : m === ENTRY_SELECTION_MODE.WEEKDAY ? 'Weekday mode' : 'DTE mode'}
                    className={cn(
                      'h-8 w-7 transition-colors',
                      active
                        ? `${BRAND_BG} text-white`
                        : 'bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200',
                      isExpanded && 'cursor-not-allowed',
                    )}
                  >
                    {m === ENTRY_SELECTION_MODE.WEEKDAY ? 'W' : 'D'}
                  </button>
                );
              })}
            </div>
          ) : null;
          const weekdayChips = DAYS.map(day => (
            <button
              key={day}
              onClick={() => handleDayToggle(strategy, day)}
              disabled={editLocked}
              title={isExpanded ? 'Locked — close the editor to change days here' : undefined}
              className={cn(
                "text-xs font-semibold w-10 h-8 rounded-md transition-all border",
                days.includes(day)
                  ? BRAND_ACTIVE_PILL
                  : "bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-700 hover:border-gray-300 hover:text-gray-600",
                isExpanded && "opacity-50 cursor-not-allowed",
              )}
            >{DAY_SHORT_LABELS[day] ?? day}</button>
          ));
          // DTE chips render in DESCENDING order (D4 → D0) so the row reads
          // left-to-right as calendar progression toward expiry day.
          const dteChips = [...INTRADAY_DTE_OPTIONS].slice().reverse().map((dte) => (
            <button
              key={dte}
              onClick={() => handleDteToggle(strategy, dte)}
              disabled={editLocked}
              title={isExpanded ? 'Locked — close the editor to change DTEs here' : INTRADAY_DTE_EDIT_LABELS[dte]}
              className={cn(
                "text-xs font-semibold w-10 h-8 rounded-md transition-all border uppercase",
                dtes.includes(dte)
                  ? BRAND_ACTIVE_PILL
                  : "bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-700 hover:border-gray-300 hover:text-gray-600",
                isExpanded && "opacity-50 cursor-not-allowed",
              )}
            >{INTRADAY_DTE_COMPACT_LABELS[dte] ?? `D${dte}`}</button>
          ));
          const dayPills = isPositional ? (
            <div className="flex items-center justify-between w-full md:w-[216px] text-xs font-semibold">
              <span className={`h-8 flex-1 rounded-md border flex items-center justify-center ${BRAND_BG} text-white ${BRAND_BORDER} shadow-sm whitespace-nowrap uppercase`}>
                Entry: {entryDTE}DTE
              </span>
              <span className="text-gray-400 px-1">→</span>
              <span className={`h-8 flex-1 rounded-md border flex items-center justify-center ${BRAND_BG} text-white ${BRAND_BORDER} shadow-sm whitespace-nowrap uppercase`}>
                Exit: {exitDTE}DTE
              </span>
            </div>
          ) : (
            <div className="relative flex items-center gap-1 flex-nowrap">
              {/* Spinner is absolutely positioned so it appears in-place during
                  optimistic save without taking column width — that keeps the
                  W/D pill + chips from shifting on save (no jerk) AND avoids
                  reserving 16 px inside the already-tight Days column. */}
              {isUpdating && (
                <RefreshCw
                  className={`absolute -left-5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 animate-spin ${BRAND_TEXT}`}
                />
              )}
              {compactModeToggle}
              {effectiveMode === ENTRY_SELECTION_MODE.DTE ? dteChips : weekdayChips}
            </div>
          );

          const isActive = strategy.status === 'ACTIVE';

          const actionBtns = (
            <div className="flex items-center gap-2">
              {onStatusToggle && (
                <EnableToggle
                  enabled={isActive}
                  loading={isUpdating}
                  disabled={isExpanded}
                  onChange={() => onStatusToggle(strategy)}
                  size="md"
                  ariaLabel={isActive ? 'Deactivate strategy' : 'Activate strategy'}
                />
              )}
              <button
                onClick={() => onEdit(strategy)}
                disabled={isUpdating || isExpanded}
                title={isExpanded ? 'Locked — close the editor to use full edit' : 'Full edit'}
                className={cn(
                  `p-2 rounded-lg text-gray-400 ${BRAND_BTN_HOVER_TONE} transition-colors`,
                  isExpanded && 'opacity-50 cursor-not-allowed hover:bg-transparent',
                )}>
                <Edit3 className="h-3.5 w-3.5" />
              </button>
              {onCopy && (
                <button
                  onClick={() => onCopy(strategy)}
                  disabled={isUpdating || isExpanded}
                  title={isExpanded ? 'Locked — close the editor to duplicate' : 'Duplicate strategy'}
                  className={cn(
                    `p-2 rounded-lg text-gray-400 ${BRAND_BTN_HOVER_TONE} transition-colors`,
                    isExpanded && 'opacity-50 cursor-not-allowed hover:bg-transparent',
                  )}>
                  <Copy className="h-3.5 w-3.5" />
                </button>
              )}
              <button
                onClick={() => onDelete(strategy)}
                disabled={isUpdating || isExpanded}
                title={isExpanded ? 'Locked — close the editor to delete' : 'Delete'}
                className={cn(
                  'p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors',
                  isExpanded && 'opacity-50 cursor-not-allowed hover:bg-transparent hover:text-gray-400',
                )}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          );

          const isStratDragging = draggedIndex === index;
          const isStratDropTarget = dragOverIndex === index && draggedIndex !== null && draggedIndex !== index;
          const stratDragHandlers = showReorder && isDesktop ? {
            draggable: true,
            onDragStart: (e: React.DragEvent) => handleDragStart(e, index),
            onDragOver: (e: React.DragEvent) => handleDragOver(e, index),
            onDragLeave: () => handleDragLeave(index),
            onDrop: (e: React.DragEvent) => handleDrop(e, index),
            onDragEnd: () => { setDraggedIndex(null); setDragOverIndex(null); },
          } : {};
          const stratRowClassExtra = cn(
            isStratDragging && "opacity-50",
            isStratDropTarget && "border-t-2 border-t-indigo-500 dark:border-t-indigo-400",
          );
          const mobileArrows = showReorder && !isDesktop ? (
            <div className="flex flex-col items-center justify-center gap-0.5 shrink-0">
              <button
                onClick={() => handleMove(index, -1)}
                disabled={index === 0}
                className="p-1 rounded-md text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Move up"
                aria-label="Move strategy up"
              >
                <ChevronUp className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => handleMove(index, 1)}
                disabled={index === strategies.length - 1}
                className="p-1 rounded-md text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Move down"
                aria-label="Move strategy down"
              >
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : null;
          const desktopDragHandle = showReorder && isDesktop ? (
            <span
              className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 cursor-grab active:cursor-grabbing shrink-0"
              title="Drag to reorder"
              aria-label="Drag handle"
            >
              <GripVertical className="h-4 w-4" />
            </span>
          ) : null;

          return (
            <div key={id} className={cn("bg-white dark:bg-gray-800", stratRowClassExtra)} {...stratDragHandlers}>

              {/* ── mobile / narrow card layout (used until xl ≥1280px) ─── */}
              <div className={cn(
                "xl:hidden px-3 py-3 space-y-2",
                isUpdating && "opacity-60",
                isExpanded && "bg-gray-100 dark:bg-gray-700"
              )}>
                {/* Row 1: badge + name + actions */}
                <div className="flex items-center gap-2">

                  {mobileArrows}
                  {expandChevron}
                  {numberBadge}
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-semibold text-gray-900 dark:text-white truncate block">{strategy.strategyName}</span>
                    {strategy.legCount > 0 && (
                      <span className="text-xs text-gray-400 dark:text-gray-500 inline-flex flex-wrap items-center gap-1.5">
                        <span>{strategy.legCount} leg{strategy.legCount !== 1 ? 's' : ''}</span>
                        {featureChips}
                      </span>
                    )}
                  </div>
                  {actionBtns}
                </div>
                {/* Row 2: time */}
                <div className="flex items-start gap-1.5 pl-10">
                  <Clock className="h-3.5 w-3.5 text-gray-400 shrink-0 mt-0.5" />
                  <div className="flex flex-col leading-tight">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                      {toHHMM(strategy.entryTime) || '--:--'} – {toHHMM(strategy.exitTime) || '--:--'}
                    </span>
                    {rangeEnd && (
                      <span className="text-[11px] font-medium text-amber-600 dark:text-amber-400" title="Range breakout end time">
                        {rangeEnd} <span className="text-gray-400 dark:text-gray-500">ORB end</span>
                      </span>
                    )}
                  </div>
                </div>
                {/* Row 3: badges */}
                <div className="flex items-center gap-2 pl-10 [&_span]:min-w-0 [&_span]:flex-1">
                  {typeBadge}
                  {indexBadge}
                  {expiryBadge}
                </div>
                {/* Row 4: days */}
                <div className="pl-10 [&_button]:flex-1">
                  {dayPills}
                </div>
              </div>

              {/* ── desktop row layout (xl ≥1280px) ──────────────────────── */}
              <div className={cn(
                "hidden xl:flex items-center gap-2 px-4 py-3.5 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors group",
                isUpdating && "opacity-60",
                isExpanded && "bg-gray-100 dark:bg-gray-700"
              )}>
                {/* number badge */}
                <div className="w-20 shrink-0 flex justify-center items-center gap-1">
                  {desktopDragHandle}
                  {expandChevron}
                  {numberBadge}
                </div>

                {/* name */}
                <div className="flex-1 min-w-[120px]">
                  <span className="text-sm font-semibold text-gray-900 dark:text-white truncate block">{strategy.strategyName}</span>
                  {strategy.legCount > 0 && (
                    <span className="text-xs text-gray-400 dark:text-gray-500 inline-flex flex-wrap items-center gap-1.5">
                      <span>{strategy.legCount} leg{strategy.legCount !== 1 ? 's' : ''}</span>
                      {featureChips}
                    </span>
                  )}
                </div>

                {/* time */}
                <div className="w-28 shrink-0 flex items-start gap-1.5">
                  <Clock className="h-3.5 w-3.5 text-gray-400 shrink-0 mt-0.5" />
                  <div className="flex flex-col leading-tight">
                    <span className="text-xs font-mono text-gray-600 dark:text-gray-300 whitespace-nowrap">
                      {toHHMM(strategy.entryTime) || '--:--'} – {toHHMM(strategy.exitTime) || '--:--'}
                    </span>
                    {rangeEnd && (
                      <span className="text-[10px] font-mono text-amber-600 dark:text-amber-400 whitespace-nowrap" title="Range breakout end time">
                        {rangeEnd} <span className="text-gray-400 dark:text-gray-500">ORB end</span>
                      </span>
                    )}
                  </div>
                </div>

                {/* type */}
                <div className="w-24 shrink-0">{typeBadge}</div>

                {/* underlying */}
                <div className="w-20 shrink-0">{indexBadge}</div>

                {/* expiry */}
                <div className="w-28 shrink-0">{expiryBadge}</div>

                {/* day pills */}
                <div className="w-72 shrink-0">{dayPills}</div>

                {/* actions */}
                <div className="w-36 shrink-0 flex items-center justify-end">
                  {actionBtns}
                </div>
              </div>

              {/* ── expand panel — full inline editor ─────────────────────── */}
              {isExpanded && fullData[id] && (
                <div className={`border-t ${BRAND_DIVIDER} bg-gray-50/60 dark:bg-gray-800/40`}>
                  <StrategyEditorPanel
                    basketId={basketId}
                    editingStrategy={fullData[id]}
                    onDone={() => { void handleInlineSaved(id); }}
                    onCancel={handleEditorCancel}
                    onDirtyChange={setEditorDirty}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <ConfirmDialog
        isOpen={pendingDiscard !== null}
        onClose={() => setPendingDiscard(null)}
        onConfirm={confirmDiscard}
        title="Discard unsaved changes?"
        confirmText="Discard"
        cancelText="Keep editing"
        variant="warning"
        message="This strategy has unsaved edits. Continuing will discard them."
      />
    </div>
  );
};

export default StrategyRowView;